import { useState, useEffect } from 'react';
import { 
  Check, Award, LayoutDashboard, ScanLine, TrendingUp, Briefcase, Video, Zap
} from 'lucide-react';

interface PremiumSidebarProps {
  currentStep: number;
  onStepClick?: (step: number) => void;
  onDashboardClick?: () => void;
  showDashboard?: boolean;
  completedSteps?: number[];
}

const steps = [
  { 
    id: 1, 
    name: 'Resume CV & ATS Scan', 
    shortName: 'ATS Scan',
    icon: ScanLine,
    color: 'from-emerald-500 to-green-600',
    lightColor: 'from-emerald-50 to-green-50',
    borderColor: 'border-emerald-200',
    description: 'Optimize your resume'
  },
  { 
    id: 2, 
    name: 'Boost Profile & LinkedIn', 
    shortName: 'Profile Boost',
    icon: TrendingUp,
    color: 'from-purple-500 to-pink-600',
    lightColor: 'from-purple-50 to-pink-50',
    borderColor: 'border-purple-200',
    description: 'Enhance your presence'
  },
  { 
    id: 3, 
    name: 'Company & Jobs Matching', 
    shortName: 'Job Matching',
    icon: Briefcase,
    color: 'from-amber-500 to-orange-600',
    lightColor: 'from-amber-50 to-orange-50',
    borderColor: 'border-amber-200',
    description: 'Find perfect matches'
  },
  { 
    id: 4, 
    name: 'Interview Preparation', 
    shortName: 'Interview Prep',
    icon: Video,
    color: 'from-rose-500 to-red-600',
    lightColor: 'from-rose-50 to-red-50',
    borderColor: 'border-rose-200',
    description: 'Ace your interviews'
  },
];

const dashboardConfig = {
  id: 0,
  name: 'Dashboard',
  subtitle: 'Overview',
  icon: LayoutDashboard,
  color: 'from-indigo-500 to-purple-600',
  lightColor: 'from-indigo-50 to-purple-50',
  borderColor: 'border-indigo-200',
  description: 'Your career hub'
};

export default function PremiumSidebar({ 
  currentStep, 
  onStepClick, 
  onDashboardClick, 
  showDashboard = true, 
  completedSteps = [] 
}: PremiumSidebarProps) {
  // Modern Navigation Button Component
  const renderNavButton = (
    config: typeof dashboardConfig | typeof steps[0],
    isActive: boolean,
    isCompleted: boolean,
    onClick?: () => void,
    isDashboard = false
  ) => {
    const IconComponent = config.icon;
    const displayName = config.name;
    const shortName = 'shortName' in config ? config.shortName : displayName;
    const moduleLabel = isDashboard ? '' : `Module ${config.id}`;
    
    return (
      <button
        onClick={onClick}
        className={`
          group relative flex items-center gap-4 w-full px-5 py-5 rounded-lg
          transition-all duration-200 ease-out
          ${isActive 
            ? `bg-gradient-to-r ${config.color} text-white shadow-md` 
            : isCompleted
            ? `bg-gradient-to-r ${config.lightColor} ${config.borderColor} border text-gray-700 hover:shadow-md`
            : `bg-white ${config.borderColor} border text-gray-600 hover:bg-gradient-to-r ${config.lightColor} hover:shadow-sm`
          }
        `}
        aria-label={displayName}
        title={displayName}
      >
        {/* Icon */}
        <div className={`
          flex items-center justify-center flex-shrink-0
          ${isActive ? 'text-white' : isCompleted ? 'text-gray-700' : 'text-gray-500'}
        `}>
          <IconComponent className="w-7 h-7" strokeWidth={2.5} />
        </div>
        
        {/* Text Content */}
        <div className="flex-1 min-w-0 text-left">
          {!isDashboard && (
            <div className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
              {moduleLabel}
            </div>
          )}
          <div className={`
            text-lg font-semibold leading-tight truncate
            ${isActive ? 'text-white' : 'text-gray-700'}
          `}>
            {shortName}
          </div>
        </div>
        
        {/* Active Indicator */}
        {isActive && (
          <div className="flex-shrink-0 w-2 h-2 bg-white rounded-full animate-pulse" />
        )}
        
        {/* Completed Indicator */}
        {isCompleted && !isActive && (
          <div className="flex-shrink-0">
            <Check className="w-5 h-5 text-green-600" strokeWidth={3} />
          </div>
        )}
      </button>
    );
  };

  return (
    <div className="w-full h-full flex flex-col bg-white rounded-2xl">
      {/* Sidebar Header */}
      <div className="px-6 py-7 border-b border-gray-200/60">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 p-3.5 rounded-xl shadow-md">
            <Zap className="h-7 w-7 text-white" strokeWidth={2.5} fill="currentColor" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">MotivaMatch</h2>
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Career Platform</p>
          </div>
        </div>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 flex flex-col gap-3 px-5 py-6 overflow-y-auto">
        {/* Dashboard Button */}
        {showDashboard && onDashboardClick && (
          <div className="mb-2">
            {renderNavButton(
              dashboardConfig,
              currentStep === 5,
              false,
              () => {
                onDashboardClick();
              },
              true
            )}
          </div>
        )}

        {/* Divider */}
        {showDashboard && (
          <div className="h-px bg-gray-200 my-2" />
        )}

        {/* Modules List */}
        {steps.map((step) => {
          const isActive = currentStep === step.id;
          const isCompleted = completedSteps.includes(step.id) || currentStep > step.id;
          
          return (
            <div key={step.id} className="mb-1">
              {renderNavButton(
                step,
                isActive,
                isCompleted,
                () => {
                  if (onStepClick) {
                    onStepClick(step.id);
                  }
                }
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
}
